import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, Spin, Alert } from 'antd';
import { ChevronDown, Search, Check } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { multichoixService } from '../../../services/multichoixService';
import { useIsMobile } from '@client/hooks/useIsMobile';
import MobileSheetBase from '@client/components/mobile/MobileSheetBase';

/**
 * Split comma-separated IDs into individual IDs
 * API returns deduplicated options with comma-separated IDs for same-label items
 */
const splitIds = (id) => {
  if (!id) return [];
  if (typeof id === 'string' && id.includes(',')) {
    return id.split(',').map((i) => i.trim()).filter(Boolean);
  }
  return [id];
};

/**
 * Check if an option is selected
 * An option with comma-separated IDs is selected if ANY of its IDs are in selectedIds
 */
const isOptionSelected = (optionId, selectedIds) => {
  const ids = splitIds(optionId);
  return ids.some((id) => selectedIds.includes(id));
};

const CulturalPracticeStepContent = ({
  step,
  value,
  selectionLabels,
  allSelections,
  onChange,
}) => {
  const { t } = useTranslation('treatment-search');
  const isMobile = useIsMobile();
  const [selectedIds, setSelectedIds] = useState(value || []);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [mobileSearchValue, setMobileSearchValue] = useState('');

  // Sync selectedIds with value prop
  useEffect(() => {
    setSelectedIds(value || []);
  }, [value]);

  // Transform selections for API
  const apiSelections = multichoixService.transformSelectionsForApi(allSelections);
  const hasCultures = apiSelections.cultures.length > 0;

  const { data, isLoading, error } = useQuery({
    queryKey: ['multichoix-step-2', apiSelections.cultures],
    queryFn: () => multichoixService.getStepOptions(2, apiSelections),
    enabled: hasCultures,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const practices = data?.options || [];

  const handleCheckAll = useCallback((checked) => {
    if (checked && practices.length > 0) {
      // Only store the first ID from each option (not all comma-separated IDs)
      // This prevents multiple IDs in URL that would show as multiple tags on cold start
      const allIds = practices.map((p) => splitIds(p.id)[0]);
      const allLabels = practices.map((p) => ({
        value: splitIds(p.id)[0],
        label: p.label,
      }));
      setSelectedIds(allIds);
      onChange(allIds, allLabels);
    } else {
      setSelectedIds([]);
      onChange([], []);
    }
  }, [practices, onChange]);

  const handleItemChange = useCallback((itemId, itemLabel, checked) => {
    // Split comma-separated IDs into individual IDs
    const itemIds = splitIds(itemId);
    const firstId = itemIds[0];

    let newIds;
    let newLabels;

    if (checked) {
      // Only store the first ID (not all comma-separated IDs)
      // This prevents multiple IDs in URL that would show as multiple tags on cold start
      newIds = [...new Set([...selectedIds, firstId])];
      newLabels = [
        ...(selectionLabels || []),
        { value: firstId, label: itemLabel },
      ];
    } else {
      // Remove all individual IDs for this option (handles legacy URLs with multiple IDs)
      newIds = selectedIds.filter((id) => !itemIds.includes(id));
      newLabels = (selectionLabels || []).filter((l) => !itemIds.includes(l.value));
    }

    setSelectedIds(newIds);
    onChange(newIds, newLabels);
  }, [selectedIds, selectionLabels, onChange]);

  // Consolidate IDs and labels on cold start
  // When URL contains multiple IDs from a comma-separated option, consolidate to just the first ID
  useEffect(() => {
    if (!practices || practices.length === 0 || !selectedIds || selectedIds.length === 0) {
      return;
    }

    // Find which practices are actually selected based on IDs
    const selectedPractices = practices.filter((p) => isOptionSelected(p.id, selectedIds));

    // Build correct IDs (only first ID per option) and labels
    const correctIds = selectedPractices.map((p) => splitIds(p.id)[0]);
    const correctLabels = selectedPractices.map((p) => ({
      value: splitIds(p.id)[0],
      label: p.label,
    }));

    // Check if IDs or labels need correction
    const idsNeedCorrection = selectedIds.length !== correctIds.length ||
      !selectedIds.every((id) => correctIds.includes(id));
    const currentLabelValues = (selectionLabels || []).map((l) => l.label).sort();
    const correctLabelValues = correctLabels.map((l) => l.label).sort();
    const labelsNeedCorrection = JSON.stringify(currentLabelValues) !== JSON.stringify(correctLabelValues);

    // If either needs correction, update both
    if (idsNeedCorrection || labelsNeedCorrection) {
      onChange(correctIds, correctLabels);
    }
  }, [practices, selectedIds]); // Only depend on practices and selectedIds, not selectionLabels/onChange

  // Count how many options are selected (checking if ANY of their IDs are selected)
  const selectedCount = practices.filter((p) => isOptionSelected(p.id, selectedIds)).length;
  const isAllSelected = practices.length > 0 && selectedCount === practices.length;
  const isIndeterminate = selectedCount > 0 && selectedCount < practices.length;

  // Mobile: Get display value
  const mobileDisplayValue = useMemo(() => {
    if (selectedCount === 0) return '';
    if (selectedCount === practices.length) return t('multichoix.steps.cultural_practice.all_selected', 'Toutes les pratiques');
    return `${selectedCount} ${t('multichoix.common.selected', 'sélectionné(s)')}`;
  }, [selectedCount, practices.length, t]);

  // Mobile: Filter options
  const filteredPractices = useMemo(() => {
    if (!mobileSearchValue) return practices;
    const search = mobileSearchValue.toLowerCase();
    return practices.filter((p) =>
      p.label.toLowerCase().includes(search)
    );
  }, [practices, mobileSearchValue]);

  if (!hasCultures) {
    return (
      <Alert
        type="info"
        message={t('multichoix.steps.cultural_practice.no_options', 'Selectionnez d\'abord une culture')}
        showIcon
      />
    );
  }

  if (error) {
    return (
      <Alert
        type="error"
        message={t('common.error', 'Une erreur est survenue')}
        description={error.message}
        showIcon
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spin tip={t('common.loading', 'Chargement...')} />
      </div>
    );
  }

  if (practices.length === 0) {
    return (
      <Alert
        type="warning"
        message={t('multichoix.steps.cultural_practice.empty', 'Aucune pratique culturale disponible pour les cultures selectionnees')}
        showIcon
        className="text-xs sm:text-sm"
      />
    );
  }

  // Mobile render
  if (isMobile) {
    return (
      <div className="w-full">
        {/* Mobile trigger */}
        <div
          onClick={() => setSheetOpen(true)}
          className="w-full min-h-[44px] px-3 py-2 border border-gray-300 rounded-lg bg-white cursor-pointer flex items-center justify-between"
        >
          <span className={`text-xs flex-1 truncate ${mobileDisplayValue ? 'text-gray-900' : 'text-gray-400'}`}>
            {mobileDisplayValue || t(step.placeholder)}
          </span>
          {isLoading ? (
            <Spin size="small" className="ml-2" />
          ) : (
            <ChevronDown size={16} className="ml-2 text-gray-400 flex-shrink-0" />
          )}
        </div>

        {/* Mobile sheet */}
        <MobileSheetBase
          isOpen={sheetOpen}
          onClose={() => setSheetOpen(false)}
          title={t(step.title)}
          showDone
          onDone={() => setSheetOpen(false)}
        >
          {/* Search */}
          <div className="sticky top-0 px-3 py-2 bg-white border-b border-gray-100 z-10 flex-shrink-0">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                type="text"
                className="w-full h-10 pl-9 pr-3 border border-gray-300 rounded-lg text-sm outline-none focus:border-[#207d54]"
                placeholder={t('common|Rechercher...')}
                value={mobileSearchValue}
                onChange={(e) => setMobileSearchValue(e.target.value)}
              />
            </div>
          </div>

          {/* Select All option */}
          <div className="px-3 py-2 border-b border-gray-100 bg-gray-50">
            <div
              className="flex items-center min-h-[44px] cursor-pointer"
              onClick={() => handleCheckAll(!isAllSelected)}
            >
              <div
                className={`w-5 h-5 border-2 rounded mr-3 flex-shrink-0 flex items-center justify-center ${
                  isAllSelected ? 'bg-[#207d54] border-[#207d54]' : isIndeterminate ? 'bg-[#207d54] border-[#207d54]' : 'border-gray-300'
                }`}
              >
                {isAllSelected && <Check size={14} className="text-white" />}
                {isIndeterminate && !isAllSelected && <div className="w-2.5 h-0.5 bg-white" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-gray-900">
                  {t('multichoix.steps.cultural_practice.select_all', 'Toutes les pratiques culturales')}
                </div>
              </div>
            </div>
          </div>

          {/* Options list */}
          <div className="flex-1 overflow-y-auto overscroll-contain">
            {isLoading && (
              <div className="flex items-center justify-center py-8">
                <Spin size="default" />
              </div>
            )}

            {!isLoading && filteredPractices.length === 0 && (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400 text-center">
                <div className="text-xs">{t('common|Aucun résultat trouvé')}</div>
              </div>
            )}

            {!isLoading && filteredPractices.length > 0 && (
              <div className="py-1">
                {filteredPractices.map((practice) => {
                  const isSelected = isOptionSelected(practice.id, selectedIds);
                  return (
                    <div
                      key={practice.id}
                      className={`flex items-center min-h-[44px] px-3 py-2 cursor-pointer ${isSelected ? 'bg-green-50' : ''}`}
                      onClick={() => handleItemChange(practice.id, practice.label, !isSelected)}
                    >
                      <div
                        className={`w-5 h-5 border-2 rounded mr-3 flex-shrink-0 flex items-center justify-center ${
                          isSelected ? 'bg-[#207d54] border-[#207d54]' : 'border-gray-300'
                        }`}
                      >
                        {isSelected && <Check size={14} className="text-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-gray-900">{practice.label}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </MobileSheetBase>
      </div>
    );
  }

  // Desktop render
  return (
    <div className="space-y-2 green-checkbox">
      <Checkbox
        checked={isAllSelected}
        indeterminate={isIndeterminate}
        onChange={(e) => handleCheckAll(e.target.checked)}
        className="font-medium text-sm sm:text-base"
      >
        <span className="text-xs sm:text-sm">
          {t('multichoix.steps.cultural_practice.select_all', 'Toutes les pratiques culturales')}
        </span>
      </Checkbox>
      <div className="pl-4 sm:pl-6 space-y-1 max-h-[250px] sm:max-h-[300px] overflow-y-auto">
        {practices.map((practice) => (
          <div key={practice.id} className="py-1">
            <Checkbox
              checked={isOptionSelected(practice.id, selectedIds)}
              onChange={(e) =>
                handleItemChange(practice.id, practice.label, e.target.checked)
              }
              className="text-xs sm:text-sm"
            >
              <span className="text-xs sm:text-sm">{practice.label}</span>
            </Checkbox>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CulturalPracticeStepContent;
